import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { QuoteStatus } from '@typesApp/QuoteData'
import { SubmitErrorResponse } from '@typesApp/SubmitResponse'
import { ConnectorErrorType, ConnectorModalType } from '@typesApp/Connector'
import { useCancelOptInRetryQuoteMutation, useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { useNullifyClaimRecord } from '@hooks/useNullifyClaimRecord'
import { claimRecordIdSelector, nullifyInProgressSelector } from '@redux/slices/claimStatusSlice'
import { setConnectorBusy, setConnectorError, showConnectorModal } from '@redux/slices/connectorSlice'
import { useAutoNullify } from '@hooks/useAutoNullify'

interface NullifyUpdateCartOptions {
  callback?: () => void
}

type UseNullifyUpdateCartReturn = (options: NullifyUpdateCartOptions) => void

/**
 * Handle nullify or cancel quote on cart update
 */
export const useNullifyUpdateCart = (): UseNullifyUpdateCartReturn => {
  const dispatch = useDispatch()
  const claimRecordId = useSelector(claimRecordIdSelector)
  const nullifyInProgress = useSelector(nullifyInProgressSelector)

  const { handleAutoNullify } = useAutoNullify()
  const { shouldNullify } = useNullifyClaimRecord()
  const [cancelQuote, cancelQuoteStatus] = useCancelOptInRetryQuoteMutation()
  const [fetchClaimRecord, fetchStatus] = useLazyGetClaimRecordQuery()

  useEffect(() => {
    if (fetchStatus.isError || cancelQuoteStatus.isError) {
      dispatch(setConnectorBusy(false))
    }
  }, [fetchStatus, cancelQuoteStatus])

  const handleError = (error: unknown) => {
    console.error(error)
    dispatchError()
  }

  const dispatchError = (type = ConnectorErrorType.GENERIC_ERROR) => {
    dispatch(setConnectorError(type))
  }

  const nullifyUpdateCart = (options: NullifyUpdateCartOptions) => {
    fetchClaimRecord(claimRecordId)
      .unwrap()
      .then(claimRecord => {
        nullifyOrCancelQuote(claimRecord, options)
      })
      .catch(handleError)
  }

  const nullifyOrCancelQuote = (claimRecord: ClaimRecord, options: NullifyUpdateCartOptions) => {
    if (shouldNullify(claimRecord.status)) {
      doNullify(options)
    } else if (shouldCancelQuote(claimRecord)) {
      doCancelQuote(options)
    } else {
      dispatch(setConnectorBusy(false))
    }
  }

  const doNullify = (options: NullifyUpdateCartOptions) => {
    // no need to call nullify, B/E will
    // automatically nullify claim record once cart is updated
    if (!nullifyInProgress) {
      handleAutoNullify({ callback: options?.callback })
    }
  }

  const shouldCancelQuote = (claimRecord: ClaimRecord): boolean => {
    return claimRecord.claimData?.quote?.status === QuoteStatus.RETRY
  }

  const doCancelQuote = (options: NullifyUpdateCartOptions) => {
    cancelQuote(claimRecordId)
      .unwrap()
      .then(() => {
        dispatch(showConnectorModal({ type: ConnectorModalType.QUOTE_CANCELLED, confirmCallback: options.callback }))
      })
      .catch(handleCancelQuoteError)
      .finally(() => dispatch(setConnectorBusy(false)))
  }

  const handleCancelQuoteError = (error: SubmitErrorResponse) => {
    if (error.status === 409) {
      console.warn('Got 409 on cancel opt-in retry (already cancelled?), claim record %s', claimRecordId, error)
      return
    }
    dispatchError()
  }

  return nullifyUpdateCart
}
